import { useState, useRef } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { HiCheck, HiChevronDown } from 'react-icons/hi'

interface PricingSectionProps {
  onCTAClick: () => void
}

const FEATURES = [
  '60+ betting markets',
  '6 major leagues',
  '31 bookmaker odds',
  'Dixon-Coles ML model',
  'Real-time live stats',
  'Predicted lineups',
  'Referee analysis',
  'Player comparisons',
]

// Example discount codes for demo
const DISCOUNT_CODES: Record<string, number> = {
  'LAUNCH20': 20,
  'EARLY10': 10,
  'FRIEND25': 25,
}

export function PricingSection({ onCTAClick }: PricingSectionProps) {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const [showDiscountInput, setShowDiscountInput] = useState(false)
  const [discountCode, setDiscountCode] = useState('')
  const [appliedDiscount, setAppliedDiscount] = useState<number | null>(null)
  const [discountError, setDiscountError] = useState(false)

  const basePrice = 7.95
  const discountedPrice = appliedDiscount
    ? (basePrice * (100 - appliedDiscount) / 100).toFixed(2)
    : null

  const handleApplyDiscount = () => {
    const code = discountCode.toUpperCase().trim()
    if (DISCOUNT_CODES[code]) {
      setAppliedDiscount(DISCOUNT_CODES[code])
      setDiscountError(false)
      // Store in localStorage for checkout
      localStorage.setItem('statscout_discount_code', code)
    } else {
      setDiscountError(true)
      setAppliedDiscount(null)
    }
  }

  return (
    <section ref={ref} className="pricing-section">
      <motion.h2
        className="pricing-title"
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5 }}
      >
        Simple, Transparent Pricing
      </motion.h2>

      <motion.p
        className="pricing-subtitle"
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        Start with a 7-day free trial. No credit card required.
      </motion.p>

      <motion.div
        className="pricing-card"
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
        transition={{ duration: 0.5, delay: 0.2 }}
        whileHover={{ scale: 1.02 }}
      >
        <div className="pricing-plan-name">PRO</div>

        <div className="pricing-price">
          {appliedDiscount ? (
            <>
              <span style={{ textDecoration: 'line-through', opacity: 0.5, fontSize: '1.5rem' }}>
                £{basePrice}
              </span>
              {' '}
              £{discountedPrice}
            </>
          ) : (
            <>£{basePrice}</>
          )}
          <span className="pricing-price-period">/month</span>
        </div>

        <div className="pricing-trial">
          after 7-day free trial
          {appliedDiscount && (
            <span style={{ display: 'block', fontSize: '0.85rem', marginTop: 4 }}>
              {appliedDiscount}% off with code {discountCode.toUpperCase()}
            </span>
          )}
        </div>

        <ul className="pricing-features">
          {FEATURES.map((feature, index) => (
            <motion.li
              key={feature}
              className="pricing-feature"
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
            >
              <HiCheck className="pricing-feature-icon" />
              <span>{feature}</span>
            </motion.li>
          ))}
        </ul>

        <button className="cta-button large pricing-cta pulse" onClick={onCTAClick}>
          Start Free Trial
        </button>

        <p className="pricing-guarantee">
          No credit card required. Cancel anytime.
        </p>

        <div className="pricing-discount-toggle">
          <button
            className="pricing-discount-btn"
            onClick={() => setShowDiscountInput(!showDiscountInput)}
          >
            Have a discount code?
            <HiChevronDown
              style={{
                transform: showDiscountInput ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
              }}
            />
          </button>

          <AnimatePresence>
            {showDiscountInput && (
              <motion.div
                className="pricing-discount-form"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <input
                  type="text"
                  className="pricing-discount-input"
                  placeholder="Enter code"
                  value={discountCode}
                  onChange={(e) => {
                    setDiscountCode(e.target.value)
                    setDiscountError(false)
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyDiscount()}
                />
                <button className="pricing-discount-apply" onClick={handleApplyDiscount}>
                  Apply
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {discountError && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{ color: 'var(--red)', fontSize: '0.85rem', marginTop: 8 }}
            >
              Invalid discount code
            </motion.p>
          )}

          {appliedDiscount && (
            <motion.p
              className="pricing-discount-success"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <HiCheck /> Discount applied!
            </motion.p>
          )}
        </div>
      </motion.div>
    </section>
  )
}
